import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import { useNavigate, Navigate } from 'react-router-dom';
import { PhotoCamera, VerifiedUser, CheckCircle } from '@mui/icons-material';
import { authClient } from '../api/client';
import { useUser } from '../context/UserContext';
import urbanImage from '../assets/urban_logo.png';
import LoadingButton from './LoadingButton';
import { ADMIN_HOME_PATH, HOME_PATH } from '../routes/routePaths';
import { userHasIneOnFile } from '../utils/normalizeUser';

const ACCENT = '#3B82F6';
const BACKGROUND_LIGHT = '#F9FAFB';
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function UploadIne(): React.ReactElement {
  const navigate = useNavigate();
  const { setTokenAndIneUploaded, isInitialized, user } = useUser();
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isInitialized) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (user?.isAdmin || userHasIneOnFile(user)) {
    return <Navigate to={user?.isAdmin ? ADMIN_HOME_PATH : HOME_PATH} replace />;
  }

  const handleFile = async (file: File | null) => {
    if (!file) {
      setBase64Image(null);
      setFileName(null);
      return;
    }
    if (!ACCEPT_TYPES.includes(file.type)) {
      setUploadError('Formato no válido. Usa PNG, JPG o PDF.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setUploadError('El archivo no debe superar 5MB.');
      return;
    }
    setUploadError(null);
    try {
      const b64 = await fileToBase64(file);
      setBase64Image(b64);
      setFileName(file.name);
    } catch {
      setUploadError('No se pudo leer el archivo.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!base64Image || !termsAccepted) return;
    setUploadError(null);
    setIsSubmitting(true);
    try {
      const { data } = await authClient.post<{ message: string; data: { token: string } }>(
        '/api/upload-ine',
        { image: base64Image }
      );
      if (data?.data?.token) {
        setTokenAndIneUploaded(data.data.token);
        navigate(user?.isAdmin ? ADMIN_HOME_PATH : HOME_PATH);
      } else {
        setUploadError('No se pudo completar el registro.');
      }
    } catch (err) {
      if (err && typeof err === 'object' && 'response' in err) {
        const ax = err as { response?: { data?: { message?: string } } };
        setUploadError(ax.response?.data?.message ?? 'Error al subir la identificación.');
      } else {
        setUploadError('Error al subir la identificación.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: BACKGROUND_LIGHT,
        color: 'text.primary',
      }}
    >
      <Box
        component="header"
        sx={{
          width: '100%',
          py: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={urbanImage}
          alt="Urban Utility Logo"
          style={{ width: 320, objectFit: 'contain', opacity: 0.9 }}
        />
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          pb: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 448,
            bgcolor: 'white',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'rgba(226, 232, 240, 1)',
          }}
        >
          <Box sx={{ px: 3, pt: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: ACCENT,
                }}
              >
                Paso 2 de 2
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}
              >
                100% Completado
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 6,
                borderRadius: 9999,
                bgcolor: 'rgba(241, 245, 249, 1)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: ACCENT,
                  borderRadius: 9999,
                }}
              />
            </Box>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.025em', mb: 0.5 }}>
              Verificación de Identidad
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 14, mb: 3 }}>
              Sube una foto clara de tu identificación oficial vigente.
            </Typography>

            <Typography
              component="label"
              sx={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                mb: 1,
                color: 'text.primary',
                textAlign: 'left',
              }}
            >
              Identificación Oficial (Frente)
            </Typography>
            <input
              ref={inputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
              onChange={handleInputChange}
              style={{ display: 'none' }}
            />
            <Box
              onClick={() => inputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? ACCENT : 'rgba(226, 232, 240, 1)',
                borderRadius: '16px',
                py: 5,
                px: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isDragging ? 'rgba(248, 250, 252, 0.8)' : 'rgba(248, 250, 252, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: ACCENT,
                  bgcolor: 'rgba(248, 250, 252, 0.8)',
                },
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 1,
                  border: '1px solid',
                  borderColor: 'rgba(241, 245, 249, 1)',
                  mb: 2,
                }}
              >
                <PhotoCamera sx={{ fontSize: 32, color: ACCENT }} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5, textAlign: 'center' }}>
                {fileName ?? 'Haz clic para subir o arrastra tu archivo'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                PNG, JPG o PDF hasta 5MB
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 2,
                mt: 2,
                bgcolor: 'rgba(59, 130, 246, 0.08)',
                borderRadius: 2,
              }}
            >
              <VerifiedUser sx={{ color: ACCENT, fontSize: 20, mt: 0.25 }} />
              <Typography variant="caption" sx={{ color: 'primary.dark', lineHeight: 1.6 }}>
                Tu información está protegida bajo estándares de cifrado bancario. Requerimos esto
                para garantizar la seguridad de la comunidad.
              </Typography>
            </Box>

            {uploadError && (
              <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(_, checked) => setTermsAccepted(checked)}
                  sx={{
                    color: 'text.secondary',
                    '&.Mui-checked': { color: ACCENT },
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Acepto los{' '}
                  <MuiLink href="#" color={ACCENT} sx={{ fontWeight: 500 }}>
                    Términos y Condiciones
                  </MuiLink>{' '}
                  y el{' '}
                  <MuiLink href="#" color={ACCENT} sx={{ fontWeight: 500 }}>
                    Aviso de Privacidad
                  </MuiLink>
                </Typography>
              }
              sx={{ mt: 2, alignItems: 'flex-start' }}
            />

            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              disableElevation
              loading={isSubmitting}
              disabled={!base64Image || !termsAccepted}
              sx={{
                mt: 2,
                py: 2,
                bgcolor: '#000',
                color: 'white',
                fontWeight: 700,
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                '&:hover': { bgcolor: '#1e293b' },
                '&.Mui-disabled': { bgcolor: '#94a3b8', color: 'white' },
              }}
            >
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                Finalizar Registro
                <CheckCircle sx={{ fontSize: 20 }} />
              </Box>
            </LoadingButton>
          </Box>

          <Box
            sx={{
              px: 3,
              pb: 3,
              pt: 2,
              textAlign: 'center',
              borderTop: '1px solid',
              borderColor: 'rgba(248, 250, 252, 1)',
            }}
          >
            <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
              ¿Necesitas ayuda con tu registro?{' '}
            </Typography>
            <MuiLink href="#" sx={{ fontSize: 12, fontWeight: 600, color: ACCENT }}>
              Soporte
            </MuiLink>
          </Box>
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: 'center',
          fontSize: 10,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
        }}
      >
        Urban Utility Guadalajara © {new Date().getFullYear()}
      </Box>
    </Box>
  );
}

export default UploadIne;
